import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { comptabiliteApi } from '../../services/comptabiliteApi';
import { formatCurrency } from '../../utils/formatters';
import { downloadCsv } from '../../utils/csv';
import { downloadFile } from '../../utils/download';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const BalanceGenerale: React.FC = () => {
  const { data, isLoading } = useQuery({ queryKey: ['compta', 'balance'], queryFn: () => comptabiliteApi.getBalance() });

  if (isLoading || !data) return <div className="p-6 text-gray-500">Calcul de la balance…</div>;

  const exportCsv = () => {
    const rows: (string | number)[][] = [['Compte', 'Intitulé', 'Débit', 'Crédit', 'Solde']];
    data.comptes.forEach((c) => rows.push([c.numero, c.intitule, num(c.debit), num(c.credit), num(c.solde)]));
    rows.push(['', 'TOTAUX', num(data.total_debit), num(data.total_credit), '']);
    downloadCsv('balance_generale.csv', rows);
  };

  return (
    <div className="w-full space-y-3">
      <div className="flex justify-end gap-2">
        <button onClick={() => downloadFile('/comptabilite/rapports/export/', 'balance_generale.xlsx', { etat: 'balance' })}
          className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm px-3 py-2 rounded-lg">
          <Download className="h-4 w-4" /> Excel
        </button>
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm px-3 py-2 rounded-lg">
          <Download className="h-4 w-4" /> CSV
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-500 uppercase">
              <th className="px-4 py-2">Compte</th>
              <th className="px-4 py-2">Intitulé</th>
              <th className="px-4 py-2 text-right">Débit</th>
              <th className="px-4 py-2 text-right">Crédit</th>
              <th className="px-4 py-2 text-right">Solde</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.comptes.map((c) => (
              <tr key={c.numero} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-mono text-gray-500">{c.numero}</td>
                <td className="px-4 py-2 text-sm text-gray-800">{c.intitule}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{formatCurrency(num(c.debit))}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{formatCurrency(num(c.credit))}</td>
                <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">{formatCurrency(num(c.solde))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="px-4 py-2" colSpan={2}>Totaux {data.equilibre ? '✓ équilibrée' : '⚠ déséquilibrée'}</td>
              <td className="px-4 py-2 text-right">{formatCurrency(num(data.total_debit))}</td>
              <td className="px-4 py-2 text-right">{formatCurrency(num(data.total_credit))}</td>
              <td className="px-4 py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default BalanceGenerale;
